import config from '../config';

export const AVATAR_PLACEHOLDER_SRC = '/avatar-placeholder.svg';

function normalizeStorageRelativePath(path) {
  if (!path || typeof path !== 'string') {
    return null;
  }

  let trimmed = path.replace(/^\//, '');
  if (/^storage\//i.test(trimmed)) {
    trimmed = trimmed.slice('storage/'.length);
  }

  return trimmed || null;
}

/**
 * Public URL for a user avatar stored on the `public` disk (e.g. user-avatars/...).
 */
export function getUserAvatarPublicUrl(avatarPathOrUrl) {
  if (!avatarPathOrUrl || typeof avatarPathOrUrl !== 'string') {
    return null;
  }

  if (/^(https?:|blob:)/i.test(avatarPathOrUrl)) {
    return avatarPathOrUrl;
  }

  const trimmed = normalizeStorageRelativePath(avatarPathOrUrl);
  if (!trimmed) {
    return null;
  }

  try {
    return `${new URL(config.apiUrl).origin}/storage/${encodeURI(trimmed)}`;
  } catch {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/storage/${encodeURI(trimmed)}`;
    }
    return null;
  }
}

/** Extract storage-relative path from a full or relative /storage/... URL (legacy API avatar_url). */
function avatarPathFromUrl(avatarUrl) {
  if (!avatarUrl || typeof avatarUrl !== 'string') {
    return null;
  }

  const storageMatch = avatarUrl.match(/\/storage\/(.+)$/);
  if (storageMatch) {
    return decodeURIComponent(storageMatch[1]);
  }

  try {
    const pathname = new URL(avatarUrl).pathname;
    const match = pathname.match(/\/storage\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function getUserAvatarUrl(user) {
  if (!user || typeof user !== 'object') {
    return null;
  }

  // Local file preview from AvatarUploadField must win over a stored avatar_path.
  if (typeof user.avatar_url === 'string' && /^blob:/i.test(user.avatar_url)) {
    return user.avatar_url;
  }

  // Same as vendor logo: always build from relative path + API origin (not APP_URL from backend).
  const path =
    user.avatar_path ??
    avatarPathFromUrl(user.avatar_url) ??
    (!/^https?:\/\//i.test(user.avatar_url ?? '') ? user.avatar_url : null);

  const baseUrl = getUserAvatarPublicUrl(path);
  if (!baseUrl) {
    return null;
  }

  // Blob preview URLs (local file selection) must not get a cache-bust query param.
  if (/^blob:/i.test(baseUrl)) {
    return baseUrl;
  }

  const cacheKey = user.updated_at ?? user.avatar_path ?? null;
  if (!cacheKey) {
    return baseUrl;
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}v=${encodeURIComponent(String(cacheKey))}`;
}

export function resolveUserAvatarSrc(user) {
  return getUserAvatarUrl(user) ?? AVATAR_PLACEHOLDER_SRC;
}
